import { useCallback, useMemo } from 'react';
import { StepStatus } from '@packages/global';
import { useScriptGenerationStore, SCRIPT_STEPS } from '../stores/scriptGenerationStore';

/**
 * 步骤导航 Hook
 * 管理步骤切换逻辑和步骤依赖验证
 */
export function useStepNavigation() {
  const {
    currentStep,
    steps,
    setCurrentStep,
    getStepStatus,
    getCompletedStepsCount,
    isAllStepsCompleted,
    isDirty
  } = useScriptGenerationStore();

  /**
   * 检查步骤是否可访问
   * 规则：
   * - 步骤 1 始终可访问
   * - 其他步骤需要前一步骤已完成或当前步骤已有数据
   */
  const isStepAccessible = useCallback((stepNumber: number): boolean => {
    if (stepNumber === 1) return true;

    const step = steps.find(s => s.stepNumber === stepNumber);
    const prevStep = steps.find(s => s.stepNumber === stepNumber - 1);

    // 如果当前步骤已有数据，可以访问
    if (step?.status !== StepStatus.PENDING) return true;

    // 如果前一步骤已完成，可以访问
    if (prevStep?.status === StepStatus.COMPLETED) return true;

    return false;
  }, [steps]);

  /**
   * 检查步骤是否需要审查
   */
  const isStepNeedsReview = useCallback((stepNumber: number): boolean => {
    return getStepStatus(stepNumber) === StepStatus.NEEDS_REVIEW;
  }, [getStepStatus]);

  /**
   * 检查步骤是否已完成
   */
  const isStepCompleted = useCallback((stepNumber: number): boolean => {
    return getStepStatus(stepNumber) === StepStatus.COMPLETED;
  }, [getStepStatus]);

  /**
   * 检查步骤是否正在处理中
   */
  const isStepInProgress = useCallback((stepNumber: number): boolean => {
    return getStepStatus(stepNumber) === StepStatus.IN_PROGRESS;
  }, [getStepStatus]);

  /**
   * 导航到指定步骤
   */
  const navigateToStep = useCallback((stepNumber: number): boolean => {
    if (!isStepAccessible(stepNumber)) {
      console.warn(`步骤 ${stepNumber} 当前不可访问`);
      return false;
    }

    // 如果有未保存的更改，可以提示用户
    if (isDirty) {
      console.warn('有未保存的更改');
      // 这里可以触发保存或提示用户
    }

    setCurrentStep(stepNumber);
    return true;
  }, [isStepAccessible, isDirty, setCurrentStep]);

  /**
   * 导航到下一步
   */
  const goToNextStep = useCallback((): boolean => {
    if (currentStep >= 6) return false;
    return navigateToStep(currentStep + 1);
  }, [currentStep, navigateToStep]);

  /**
   * 导航到上一步
   */
  const goToPrevStep = useCallback((): boolean => {
    if (currentStep <= 1) return false;
    return navigateToStep(currentStep - 1);
  }, [currentStep, navigateToStep]);

  /**
   * 获取下一个需要处理的步骤
   */
  const getNextIncompleteStep = useCallback((): number | null => {
    for (let i = 1; i <= 6; i++) {
      const status = getStepStatus(i);
      if (status === StepStatus.PENDING || status === StepStatus.NEEDS_REVIEW) {
        return i;
      }
    }
    return null;
  }, [getStepStatus]);

  /**
   * 获取第一个需要审查的步骤
   */
  const getFirstReviewStep = useCallback((): number | null => {
    for (let i = 1; i <= 6; i++) {
      if (getStepStatus(i) === StepStatus.NEEDS_REVIEW) {
        return i;
      }
    }
    return null;
  }, [getStepStatus]);

  /**
   * 步骤信息列表（带状态）
   */
  const stepsWithStatus = useMemo(() => {
    return SCRIPT_STEPS.map(({ stepNumber, name, key }) => {
      const status = getStepStatus(stepNumber);
      return {
        stepNumber,
        name,
        key,
        status,
        isAccessible: isStepAccessible(stepNumber),
        isCompleted: status === StepStatus.COMPLETED,
        isNeedsReview: status === StepStatus.NEEDS_REVIEW,
        isInProgress: status === StepStatus.IN_PROGRESS,
        isCurrent: stepNumber === currentStep
      };
    });
  }, [currentStep, getStepStatus, isStepAccessible]);

  /**
   * 进度信息
   */
  const progress = useMemo(() => {
    const completed = getCompletedStepsCount();
    const total = 6;
    const percentage = Math.round((completed / total) * 100);
    const allCompleted = isAllStepsCompleted();

    return {
      completed,
      total,
      percentage,
      allCompleted
    };
  }, [getCompletedStepsCount, isAllStepsCompleted]);

  /**
   * 当前步骤信息
   */
  const currentStepInfo = useMemo(() => {
    return stepsWithStatus.find(s => s.stepNumber === currentStep) || null;
  }, [stepsWithStatus, currentStep]);

  /**
   * 是否可以前进
   */
  const canGoNext = useMemo(() => {
    return currentStep < 6 && isStepAccessible(currentStep + 1);
  }, [currentStep, isStepAccessible]);

  /**
   * 是否可以后退
   */
  const canGoPrev = useMemo(() => {
    return currentStep > 1;
  }, [currentStep]);

  return {
    // 当前状态
    currentStep,
    currentStepInfo,
    stepsWithStatus,
    progress,

    // 导航方法
    navigateToStep,
    goToNextStep,
    goToPrevStep,

    // 状态检查
    isStepAccessible,
    isStepCompleted,
    isStepNeedsReview,
    isStepInProgress,
    canGoNext,
    canGoPrev,

    // 辅助方法
    getNextIncompleteStep,
    getFirstReviewStep
  };
}

export type UseStepNavigationReturn = ReturnType<typeof useStepNavigation>;
